"use client";

import { useEffect, useState } from "react";
import { motion, useScroll, useTransform, useReducedMotion, useMotionValueEvent } from "framer-motion";
import { usePortfolioSequence } from "./PortfolioSequence";
import styles from "./cosmic.module.css";

export default function CosmicBackground() {
  const { phase, setPhase } = usePortfolioSequence();
  const { scrollY } = useScroll();
  const reducedMotion = useReducedMotion();
  const [departing, setDeparting] = useState(false);
  useMotionValueEvent(scrollY, "change", () => {
    const portfolio = document.getElementById("portfolio");
    if (!portfolio) return;
    const top = portfolio.getBoundingClientRect().top;
    // Hysteresis prevents replay when the scroll position jitters at the boundary.
    setDeparting(previous => top < window.innerHeight * (previous ? 0.95 : 0.8));
  });
  useEffect(() => {
    if (departing) setPhase(current => current === "idle" ? (reducedMotion ? "done" : "horizon") : current);
  }, [departing, reducedMotion, setPhase]);
  const [viewportHeight, setViewportHeight] = useState(800);
  const [compact, setCompact] = useState(false);
  const progress = useTransform(scrollY, [0, viewportHeight * 0.6], [0, 1]);
  // Starting light is 10% brighter than the previous 0.7 setting; retain the final light.
  const brightness = useTransform(progress, [0, 1], ["brightness(0.77)", "brightness(1.3)"]);
  const glow = useTransform(progress, [0, 1], [0.8, 1]);
  const glowScale = useTransform(progress, [0, 1], [1, compact ? 1.9 : 1.45]);
  const rim = useTransform(progress, [0, 1], [0.8, 1]);

  useEffect(() => {
    const resize = () => {
      setViewportHeight(window.innerHeight);
      setCompact(window.matchMedia("(max-width: 767px)").matches);
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  // Viewport-relative stars radiate from the centre, including on portrait screens.
  const stars = Array.from({ length: 96 }, (_, i) => ({
    x: ((i % 12) + 0.2 + ((i * 37) % 61) / 100) * (100 / 12),
    y: (Math.floor(i / 12) + 0.2 + ((i * 23) % 61) / 100) * (100 / 8),
    size: i % 9 === 0 ? 2 : 1,
  }));

  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden"
    >
      <motion.div
        className="absolute inset-0"
        initial={false}
        animate={{ opacity: ["stars", "done"].includes(phase) ? 0 : 1 }}
        transition={{ duration: 0 }}
      >
        {stars.map((star, i) => <span key={i} className="absolute rounded-full bg-white" style={{ left: `${star.x}%`, top: `${star.y}%`, width: star.size, height: star.size, opacity: 0.35 + (i % 5) * 0.13 }} />)}
      </motion.div>
      {phase === "stars" && !reducedMotion && (
        <motion.div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden" data-portfolio-star-exit
          initial={{ opacity: 1 }} animate={{ opacity: [1, 1, 0] }}
          transition={{ duration: 1.9, times: [0, 0.85, 1] }}
          onAnimationComplete={() => setPhase("done")}>
          {stars.map((star, i) => (
            <motion.span key={i} className="absolute rounded-full bg-white shadow-[0_0_4px_#b9e3ff]"
              style={{ left: `${star.x}%`, top: `${star.y}%`, width: star.size + 0.5, height: star.size + 0.5, opacity: 0.6 + (i % 3) * 0.15 }}
              initial={{ x: 0, y: 0 }}
              animate={{ x: `${(star.x - 50) * 3}vw`, y: `${(star.y - 50) * 3}vh` }}
              transition={{ delay: 0.35, duration: 1.55, ease: [0.55, 0, 0.85, 0.45] }}
            />
          ))}
        </motion.div>
      )}
      <motion.div
        className={styles.horizon}
        style={{ filter: brightness, transformPerspective: 1000, transformOrigin: "50% 20%" }}
        initial={false}
        animate={{ rotateX: departing && !reducedMotion ? 75 : 0, y: departing && !reducedMotion ? "-20%" : "0%", opacity: departing ? 0 : 1 }}
        onAnimationComplete={() => { if (departing) setPhase(current => current === "horizon" ? "ready" : current); }}
        transition={{ duration: reducedMotion ? 0 : 0.65, ease: [0.4, 0, 0.2, 1] }}
      >
        <motion.div className={styles.glowEnvelope} style={{ opacity: glow, scale: glowScale }}>
          <div className={styles.earthGlow} />
        </motion.div>
        <div className={styles.earth} />
        <motion.div className={styles.earthRim} style={{ opacity: rim }} />
      </motion.div>
    </div>
  );
}
